import { useState, useEffect } from 'react';
import type { EvaluationAuditTrail } from '@amzn/helianthus-arbiter-client';
import { ResourceNotFoundException } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../context/ArbiterClientContext';

interface UseEvaluationAuditTrailResult {
  auditTrail: EvaluationAuditTrail | null;
  loading: boolean;
  error: Error | null;
  isNotFound: boolean;
}

/**
 * Fetch the evaluation audit trail for a given evaluation.
 *
 * Returns ``isNotFound: true`` when the backend responds with 404
 * (pre-LookingGlass evaluations or in-progress evaluations that
 * have not generated an audit trail yet).
 */
export function useEvaluationAuditTrail(
  evaluationId: string | undefined,
  options?: { enabled?: boolean },
): UseEvaluationAuditTrailResult {
  const client = useArbiterClient();
  const [auditTrail, setAuditTrail] = useState<EvaluationAuditTrail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    if (!evaluationId) return;
    if (options?.enabled === false) return;

    let cancelled = false;

    const fetchAuditTrail = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      setIsNotFound(false);
      setAuditTrail(null);

      try {
        const response = await client.getEvaluationAuditTrail({ evaluationId });
        if (!cancelled && response.auditTrail) {
          setAuditTrail(response.auditTrail);
        }
      } catch (err) {
        if (cancelled) return;

        if (err instanceof ResourceNotFoundException) {
          setIsNotFound(true);
        } else {
          setError(err instanceof Error ? err : new Error('Failed to fetch audit trail'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAuditTrail();

    return () => {
      cancelled = true;
    };
  }, [evaluationId, client, options?.enabled]);

  return { auditTrail, loading, error, isNotFound };
}
